/**
 * Bridges the family location's deadline policy + the week's submitted
 * orders into the per-day status badges. Lives in the week-component layer
 * (may reference `@/api` types) so the pure status logic in
 * `@/lib/family-orders/day-status` stays decoupled.
 *
 * Under FIXED_WEEKLY one cutoff governs the whole service week; under
 * RELATIVE_DAYS each service day carries its own cutoff ("N days before the
 * service date"). `deadlineStatus` handles both when given the actual service
 * day, so badges are always computed per day — FIXED_WEEKLY simply yields the
 * same instant for every day of the week.
 */

import type { DayOfWeek, OrderDeadlineMode } from '@ordo/recurrence'

import type { FamilyOrderWire } from '@/api/family-orders'
import {
  type DayBadgeTone,
  dayBadgeTone,
  type DayOrderStatus,
  formatDayStatusLabel,
  resolveDayStatus,
} from '@/lib/family-orders/day-status'
import { addDaysKey, todayKey } from '@/lib/school-orders/calendar'
import {
  type DeadlineStatus,
  deadlineStatus,
  orderClosedReason,
  type SchoolDeadlinePolicy,
} from '@/lib/school-orders/deadline'

import { acceptsParentOrders, type FamilyOrderLocation } from './columns'

export interface DayBadge {
  status: DayOrderStatus
  label: string
  tone: DayBadgeTone
  /** THIS day's cutoff instant — null when the campus has no deadline policy. */
  deadline: Date | null
  /** The cutoff falls today in the campus's timezone (label shows a time). */
  deadlineToday: boolean
}

/** Whether two instants fall on the same calendar day in `timeZone`. */
function isSameDayInTz(a: Date, b: Date, timeZone: string): boolean {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
  return fmt.format(a) === fmt.format(b)
}

function policyFor(location: FamilyOrderLocation): SchoolDeadlinePolicy {
  return {
    orderDeadlineMode: (location.orderDeadlineMode ?? 'FIXED_WEEKLY') as OrderDeadlineMode,
    orderDeadlineDayOfWeek: location.orderDeadlineDayOfWeek as DayOfWeek | null,
    orderDeadlineWeeksBefore: location.orderDeadlineWeeksBefore,
    orderDeadlineTime: location.orderDeadlineTime,
    orderDeadlineRelativeDays: location.orderDeadlineRelativeDays,
    timeZone: location.timeZone,
  }
}

/**
 * The cutoff after which NOTHING in the week starting `mondayKey` is
 * orderable. FIXED_WEEKLY: the single weekly cutoff. RELATIVE_DAYS: each day
 * closes on its own, so the week as a whole closes with its LAST service day
 * (Friday) — anchoring to Monday would gray out a week whose later days are
 * still open.
 */
export function weekDeadlineFor(location: FamilyOrderLocation, mondayKey: string, now?: Date): DeadlineStatus {
  const policy = policyFor(location)
  const anchorKey = policy.orderDeadlineMode === 'RELATIVE_DAYS' ? addDaysKey(mondayKey, 4) : mondayKey
  return deadlineStatus(anchorKey, policy, now)
}

/**
 * Whether ordering for a specific service day is closed — its deadline has
 * passed OR the day is in the past. Past days are always closed, even when
 * no deadline policy is set. Drives the day view's read-only gate.
 */
export function isDayClosed(location: FamilyOrderLocation, dayKey: string, now?: Date): boolean {
  return orderClosedReason(dayKey, policyFor(location), todayKey(now), now) !== null
}

/**
 * The earliest Monday at or after `fromMonday` whose order window is still
 * open — the week the calendar should land on by default, so a parent isn't
 * dropped onto an already-closed week. Falls back to `fromMonday` if no open
 * week is found within `maxWeeks` (e.g. no deadline policy keeps every week
 * open, returning `fromMonday` immediately).
 */
export function nextOpenWeekMonday(
  location: FamilyOrderLocation,
  fromMonday: string,
  maxWeeks = 8,
  now?: Date,
): string {
  let monday = fromMonday
  for (let i = 0; i < maxWeeks; i += 1) {
    if (!weekDeadlineFor(location, monday, now).closed) {
      return monday
    }
    monday = addDaysKey(monday, 7)
  }
  return fromMonday
}

/**
 * Actionability ranking for merging per-campus badges into one day pill.
 * "You can still order" beats "you can still edit" beats closed states —
 * the merged pill should always surface the strongest remaining action
 * across the family's campuses.
 */
const STATUS_PRIORITY: Record<DayOrderStatus, number> = {
  PAYMENT_PENDING: 5,
  ORDER_BY: 4,
  EDIT_BY: 3,
  COMPLETE: 2,
  NOT_ORDERED: 1,
  NONE: 0,
}

/**
 * Merge one badge set per campus into a single per-day set — the family
 * week view aggregates every child regardless of campus, so a day's pill
 * reflects the most actionable status across their schools.
 */
export function mergeDayBadges(perLocation: ReadonlyArray<Record<string, DayBadge>>): Record<string, DayBadge> {
  const merged: Record<string, DayBadge> = {}
  for (const badges of perLocation) {
    for (const [dk, badge] of Object.entries(badges)) {
      const current = merged[dk]
      if (!current || STATUS_PRIORITY[badge.status] > STATUS_PRIORITY[current.status]) {
        merged[dk] = badge
      }
    }
  }
  return merged
}

/** Badges per day merged across every campus the family's children attend. */
export function buildMergedDayBadges(
  locations: readonly FamilyOrderLocation[],
  weekKeys: string[],
  orders: readonly FamilyOrderWire[],
  now?: Date,
): Record<string, DayBadge> {
  return mergeDayBadges(locations.map((location) => buildDayBadges(location, weekKeys, orders, now).badges))
}

/**
 * The earliest still-open Monday across every campus that takes parent
 * orders. A view-only campus (school ordering only) has no order window to
 * chase, so it never moves the default week: a fully view-only family lands
 * on `fromMonday` — this week's menu.
 */
export function nextOpenWeekMondayAll(
  locations: readonly FamilyOrderLocation[],
  fromMonday: string,
  maxWeeks = 8,
  now?: Date,
): string {
  const candidates = locations
    .filter(acceptsParentOrders)
    .map((location) => nextOpenWeekMonday(location, fromMonday, maxWeeks, now))
  return candidates.length > 0 ? candidates.reduce((a, b) => (a <= b ? a : b)) : fromMonday
}

/**
 * A week is fully closed only when it's closed at EVERY campus that takes
 * parent orders. A view-only campus never closes a week; with no orderable
 * campus at all, nothing is "closed".
 */
export function weekClosedForAll(locations: readonly FamilyOrderLocation[], mondayKey: string, now?: Date): boolean {
  const orderable = locations.filter(acceptsParentOrders)
  return orderable.length > 0 && orderable.every((location) => weekDeadlineFor(location, mondayKey, now).closed)
}

/**
 * A view-only campus's day (school ordering only, PROD-1554): no deadline,
 * nothing to order, so no pill. `NONE` ranks lowest in {@link mergeDayBadges},
 * so in a mixed household the orderable sibling's pill always wins.
 */
const VIEW_ONLY_DAY_BADGE: DayBadge = {
  status: 'NONE',
  label: '',
  tone: 'neutral',
  deadline: null,
  deadlineToday: false,
}

/**
 * Per-day badges for a visible week. Each day's status is resolved against
 * that day's own cutoff (identical across the week under FIXED_WEEKLY) plus
 * whether the day has a (still-editable) order.
 */
export function buildDayBadges(
  location: FamilyOrderLocation,
  weekKeys: string[],
  orders: readonly FamilyOrderWire[],
  now?: Date,
): { weekDeadline: DeadlineStatus; badges: Record<string, DayBadge> } {
  const policy = policyFor(location)
  const weekDeadline = weekDeadlineFor(location, weekKeys[0] ?? '', now)
  if (!acceptsParentOrders(location)) {
    return { weekDeadline, badges: Object.fromEntries(weekKeys.map((dk) => [dk, { ...VIEW_ONLY_DAY_BADGE }])) }
  }
  const byDate = new Map<string, { hasOrder: boolean; editable: boolean; paymentPending: boolean }>()
  for (const order of orders) {
    // Cancelled batches never drive a badge (server excludes them from
    // listWeek; guard kept for stale caches). Neither do refunded /
    // all-lines-cancelled batches (derived `state`) — a day whose only order
    // was refunded is not "Complete", and re-opens for ordering if the
    // window allows.
    if (order.status === 'VOIDED' || (order.state ?? 'active') !== 'active') {
      continue
    }
    const dk = order.serviceDate.slice(0, 10)
    const cur = byDate.get(dk) ?? { hasOrder: false, editable: false, paymentPending: false }
    cur.hasOrder = true
    cur.editable = cur.editable || order.editable
    cur.paymentPending = cur.paymentPending || order.status === 'DRAFT'
    byDate.set(dk, cur)
  }
  const today = todayKey(now)
  const badges: Record<string, DayBadge> = {}
  for (const dk of weekKeys) {
    const dayDeadline = deadlineStatus(dk, policy, now)
    const deadlineToday = dayDeadline.deadline
      ? isSameDayInTz(dayDeadline.deadline, now ?? new Date(), location.timeZone)
      : false
    const info = byDate.get(dk) ?? { hasOrder: false, editable: false, paymentPending: false }
    const status = resolveDayStatus({
      deadline: dayDeadline,
      hasOrder: info.hasOrder,
      editable: info.editable,
      paymentPending: info.paymentPending,
      // Today counts as past only when no cutoff is provisioned for it — the
      // implicit cutoff is then the day's local midnight, already behind us.
      dayIsPast: dk < today || (dk === today && dayDeadline.deadline === null),
    })
    badges[dk] = {
      status,
      label: formatDayStatusLabel(status, dayDeadline.deadline, location.timeZone, deadlineToday),
      tone: dayBadgeTone(status, deadlineToday),
      deadline: dayDeadline.deadline,
      deadlineToday,
    }
  }
  return { weekDeadline, badges }
}
